import { useEffect, useId, useRef, useState, type FormEvent } from 'react';

/**
 * The queue's search (a ticket number or its start, or words of the title), sent on Enter so platform-api is not asked
 * on every keystroke. ⌘K (Ctrl+K elsewhere) focuses it from anywhere on the page, as in Linear; Escape clears it.
 */
export function TicketSearch({ value, onSearch }: { value: string; onSearch: (q: string) => void }) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [text, setText] = useState(value);
  // A new address (a view link, back) shows its own search.
  const [shown, setShown] = useState(value);
  if (shown !== value) {
    setShown(value);
    setText(value);
  }
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 'k') {
        // Inside a dialog its own fields keep the keyboard.
        if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
        // Risky, so spelled out: NavigationShell binds ⌘K to its page search. On the queue, ⌘K searches tickets (the
        // spec's Linear behaviour), so this listener runs first (window, capture phase) and stops the event there;
        // everywhere else in Admin the shell keeps ⌘K. The shell's search stays reachable from its own button.
        event.preventDefault();
        event.stopPropagation();
        input.current?.focus();
        input.current?.select();
      }
    };
    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true });
  }, []);
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSearch(text.trim().slice(0, 200));
  };
  return (
    <form role="search" className="admin-ticket-search" onSubmit={submit}>
      <label htmlFor={id} className="admin-sr-only">
        Search tickets
      </label>
      <input
        ref={input}
        id={id}
        className="admin-input"
        type="search"
        value={text}
        maxLength={200}
        placeholder="Search number or title  ⌘K"
        spellCheck={false}
        autoComplete="off"
        onChange={(event) => setText(event.currentTarget.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && text) {
            event.preventDefault();
            setText('');
            onSearch('');
          }
        }}
      />
    </form>
  );
}
